import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import JeaButton from "@/components/jea/JeaButton";
import { apiClient, formatApiError } from "@/lib/apiClient";
import { relativeTime } from "@/lib/dates";
import { useRolePermissions } from "@/hooks/useRolePermissions";

const NotesTab = ({ debtorId }) => {
  const { canWriteNotes } = useRolePermissions();
  const [notes, setNotes] = useState([]);
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get(`/debtors/${debtorId}/notes`);
      setNotes(data.notes || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [debtorId]);

  const add = async () => {
    if (!body.trim()) return;
    setSubmitting(true);
    try {
      await apiClient.post(`/debtors/${debtorId}/notes`, { body: body.trim() });
      setBody("");
      toast.success("Note added.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id) => {
    try {
      await apiClient.delete(`/debtors/${debtorId}/notes/${id}`);
      toast.success("Note deleted.");
      setNotes((prev) => prev.filter((n) => n.id !== id));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (loading) return null;

  return (
    <div className="space-y-5" data-testid="notes-tab">
      {canWriteNotes && (
        <div className="space-y-3">
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Add a note about this debtor..."
            className="w-full min-h-[120px] rounded-[8px] bg-bg-tertiary border border-border-default focus:border-accent-pink focus:shadow-pink-glow p-3 text-[14px] text-white placeholder:text-text-muted focus:outline-none transition-all"
            data-testid="notes-body-input"
          />
          <div className="flex justify-end">
            <JeaButton onClick={add} loading={submitting} disabled={!body.trim()} data-testid="notes-add-cta">
              Add Note
            </JeaButton>
          </div>
        </div>
      )}

      {notes.length === 0 ? (
        <div className="bg-bg-secondary border border-border-default rounded-[12px] p-8 text-center text-text-secondary text-[14px]" data-testid="notes-empty">
          No notes yet.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="notes-list">
          {notes.map((n) => (
            <li
              key={n.id}
              className="group relative bg-bg-tertiary rounded-[8px] p-4"
              data-testid={`note-item-${n.id}`}
            >
              <div className="text-[12px] text-text-muted jea-mono absolute top-3 right-3">
                {relativeTime(n.created_at)}
              </div>
              {canWriteNotes && (
                <button
                  type="button"
                  onClick={() => remove(n.id)}
                  className="absolute top-3 right-14 p-1 text-text-secondary hover:text-danger opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-label="Delete note"
                  data-testid={`note-delete-${n.id}`}
                >
                  <X size={14} />
                </button>
              )}
              <p className="text-white text-[14px] whitespace-pre-line pr-24">{n.body}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default NotesTab;
